import { useState } from "react";
import { Reveal, Eyebrow } from "./Reveal";
import { Check, ShieldCheck } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const tiers = [
  {
    key: "solo",
    name: "Solo Fleet",
    monthly: 349,
    annual: 279,
    tag: "1–3 trucks",
    features: [
      "Up to 60 recovered conversations / mo",
      "Instant branded SMS response",
      "Smart qualification flow",
      "Email + SMS weekly report",
      "Review request automation",
    ],
  },
  {
    key: "growth",
    name: "Growth Contractor",
    monthly: 749,
    annual: 599,
    tag: "4–12 trucks",
    popular: true,
    features: [
      "Unlimited recovered conversations",
      "Human dispatcher escalation",
      "Seasonal reactivation campaigns",
      "Custom scripting & pricing guardrails",
      "Live analytics dashboard",
      "Forgotten-estimate follow-up campaigns",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise Fleet",
    monthly: 1499,
    annual: 1199,
    tag: "13+ trucks · multi-location",
    features: [
      "Everything in Growth",
      "Multi-location routing rules",
      "Dedicated success manager",
      "Custom integrations & API access",
      "Quarterly revenue-recovery audits",
    ],
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(true);

  return (
    <section id="pricing" data-testid="pricing-section" className="border-t border-white/[0.06] bg-[#0E1117] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="text-center">
          <Eyebrow>Pricing</Eyebrow>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Costs less than one recovered job per month
          </h2>
          <div
            data-testid="pricing-toggle"
            className="mx-auto mt-8 inline-flex items-center rounded-full border border-white/[0.1] bg-[#12161F] p-1"
          >
            {[
              { label: "Monthly", value: false },
              { label: "Annual · save 20%", value: true },
            ].map((o) => (
              <button
                key={o.label}
                data-testid={o.value ? "toggle-annual" : "toggle-monthly"}
                onClick={() => setAnnual(o.value)}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-all duration-200 ${
                  annual === o.value
                    ? "bg-[#F25F22] text-white shadow-[0_0_18px_rgba(242,95,34,0.4)]"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {tiers.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08} className="h-full">
              <div
                data-testid={`pricing-card-${i}`}
                className={`relative flex h-full flex-col rounded-xl border p-8 transition-all duration-300 hover:-translate-y-1 ${
                  t.popular
                    ? "border-[#F25F22]/60 bg-[#151A25] shadow-[0_0_50px_rgba(242,95,34,0.12)]"
                    : "border-white/[0.08] bg-[#12161F] hover:border-[#F25F22]/30"
                }`}
              >
                {t.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#F25F22] px-4 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-white">
                    Most popular
                  </span>
                )}
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                  {t.tag}
                </p>
                <h3 className="mt-2 font-display text-xl font-bold text-slate-50">
                  {t.name}
                </h3>
                <div className="mt-5 flex items-baseline gap-1.5">
                  <span
                    data-testid={`pricing-price-${i}`}
                    className="font-display text-4xl font-bold text-slate-50"
                  >
                    ${annual ? t.annual : t.monthly}
                  </span>
                  <span className="text-sm text-slate-500">/ mo</span>
                </div>
                <p className="mt-1 font-mono text-[11px] text-slate-500">
                  {annual ? "billed annually" : "billed monthly"}
                </p>
                <ul className="mt-7 flex-1 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#F25F22]" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={`/signup?plan=${t.key}&billing=${annual ? "annual" : "monthly"}`}
                  data-testid={`pricing-cta-${i}`}
                  className={`mt-8 block rounded-md py-3 text-center text-sm font-semibold transition-all duration-200 ${
                    t.popular
                      ? "bg-[#F25F22] text-white hover:bg-[#FF7539] hover:shadow-[0_0_24px_rgba(242,95,34,0.45)]"
                      : "border border-white/[0.12] bg-white/[0.03] text-slate-200 hover:border-[#F25F22]/50"
                  }`}
                >
                  Get {t.name}
                </a>
                {t.popular && (
                  <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-emerald-400">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Revenue guarantee: recover your fee in 90 days or we work free
                  </p>
                )}
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-10 text-center text-sm text-slate-500">
            Not ready to pick a plan?{" "}
            <a
              href="/demo"
              data-testid="pricing-demo-link"
              className="font-semibold text-[#F25F22] underline-offset-4 transition-colors hover:text-[#FF7539] hover:underline"
            >
              Watch the automatic demo
            </a>{" "}
            or{" "}
            <a
              href="/playground"
              data-testid="pricing-playground-link"
              className="font-semibold text-[#F25F22] underline-offset-4 transition-colors hover:text-[#FF7539] hover:underline"
            >
              drive it yourself
            </a>{" "}
            — your company name, your trade, booked job in 30 seconds.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
